import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, FileText, Truck, ShoppingCart, 
  Calendar, MapPin, User, CreditCard, ExternalLink,
  Package, Clock, Hash, Printer, CheckCircle2
} from 'lucide-react';
import { factures, bonsCommande, bonsLivraison, formatMontant, formatDate, getStatutLabel, getStatutColor } from '../data/mockData';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const allDocs = [...factures, ...bonsCommande, ...bonsLivraison];
  const doc = allDocs.find(d => d.id === id);

  if (!doc) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <FileText size={48} strokeWidth={1} />
          <h3>Document introuvable</h3>
          <p>Le document {id} n'existe pas.</p>
          <button className="btn-primary" onClick={() => navigate('/documents')}>
            <ArrowLeft size={16} /> Retour aux documents
          </button>
        </div>
      </div>
    );
  }

  const isFacture = doc.type === 'facture';
  const isBC = doc.type === 'bon_commande';
  const isBL = doc.type === 'bon_livraison';
  const statutStyle = getStatutColor(doc.statut);

  const getTypeLabel = () => {
    if (isFacture) return 'Facture';
    if (isBC) return 'Bon de commande';
    if (isBL) return 'Bon de livraison';
    return 'Document';
  };

  const getTypeIcon = () => {
    if (isFacture) return <FileText size={20} />;
    if (isBC) return <ShoppingCart size={20} />;
    if (isBL) return <Truck size={20} />;
    return <FileText size={20} />;
  };

  const getTypeColor = () => {
    if (isFacture) return 'facture-color-bg';
    if (isBC) return 'commande-color-bg';
    if (isBL) return 'livraison-color-bg';
    return '';
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/documents')}>
          <ArrowLeft size={18} />
          <span>Retour aux documents</span>
        </button>
        <div className="detail-header-actions">
          <button className="btn-secondary">
            <Printer size={16} /> Imprimer
          </button>
          <button className="btn-primary">
            <Download size={16} /> Télécharger PDF
          </button>
        </div>
      </div>

      {/* Titre document */}
      <div className="detail-title-card">
        <div className="detail-title-left">
          <div className={`detail-type-badge ${getTypeColor()}`}>
            {getTypeIcon()}
            <span>{getTypeLabel()}</span>
          </div>
          <h1>{doc.id}</h1>
          <p className="detail-ref">{doc.reference}</p>
        </div>
        <div className="detail-title-right">
          <span 
            className="doc-status-badge doc-status-badge-lg"
            style={{ background: statutStyle.bg, color: statutStyle.color, borderColor: statutStyle.border }}
          >
            {doc.statut === 'livre' && <CheckCircle2 size={14} />}
            {getStatutLabel(doc.statut)}
          </span>
          {isFacture && (
            <div className="detail-amount-block">
              <span className="detail-amount-label">Montant TTC</span>
              <span className="detail-amount-value">{formatMontant(doc.montantTTC)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="detail-grid">
        {/* Informations générales */}
        <div className="detail-card">
          <h2 className="detail-card-title">
            <Hash size={18} /> Informations
          </h2>
          <div className="detail-info-list">
            <div className="detail-info-row">
              <span className="detail-info-label"><Calendar size={14} /> Date</span>
              <span className="detail-info-value">{formatDate(doc.date)}</span>
            </div>
            {isFacture && (
              <>
                <div className="detail-info-row">
                  <span className="detail-info-label"><Clock size={14} /> Échéance</span>
                  <span className="detail-info-value">{formatDate(doc.echeance)}</span>
                </div>
                {doc.bonCommande && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><ShoppingCart size={14} /> Bon de commande</span>
                    <button className="link-text" onClick={() => navigate(`/documents/${doc.bonCommande}`)}>
                      {doc.bonCommande}
                    </button>
                  </div>
                )}
                {doc.bonLivraison && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><Truck size={14} /> Bon de livraison</span>
                    <button className="link-text" onClick={() => navigate(`/documents/${doc.bonLivraison}`)}>
                      {doc.bonLivraison}
                    </button>
                  </div>
                )}
              </>
            )}
            {isBC && (
              <>
                <div className="detail-info-row">
                  <span className="detail-info-label"><MapPin size={14} /> Carrière</span>
                  <span className="detail-info-value">{doc.carriere}</span>
                </div>
                {doc.facture && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><FileText size={14} /> Facture</span>
                    <button className="link-text" onClick={() => navigate(`/documents/${doc.facture}`)}>
                      {doc.facture}
                    </button>
                  </div>
                )}
                {doc.bonLivraison && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><Truck size={14} /> Bon de livraison</span>
                    <button className="link-text" onClick={() => navigate(`/documents/${doc.bonLivraison}`)}>
                      {doc.bonLivraison}
                    </button>
                  </div>
                )}
              </>
            )}
            {isBL && (
              <>
                <div className="detail-info-row">
                  <span className="detail-info-label"><MapPin size={14} /> Carrière</span>
                  <span className="detail-info-value">{doc.carriere}</span>
                </div>
                <div className="detail-info-row">
                  <span className="detail-info-label"><MapPin size={14} /> Adresse livraison</span>
                  <span className="detail-info-value">{doc.adresseLivraison}</span>
                </div>
                <div className="detail-info-row">
                  <span className="detail-info-label"><User size={14} /> Chauffeur</span>
                  <span className="detail-info-value">{doc.chauffeur} ({doc.immatriculation})</span>
                </div>
                <div className="detail-info-row">
                  <span className="detail-info-label"><Clock size={14} /> Horaires</span>
                  <span className="detail-info-value">Départ {doc.heureDepart} — Arrivée {doc.heureArrivee}</span>
                </div>
                <div className="detail-info-row">
                  <span className="detail-info-label"><User size={14} /> Signataire</span>
                  <span className="detail-info-value">{doc.signataireClient}</span>
                </div>
                {doc.observations && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><FileText size={14} /> Observations</span>
                    <span className="detail-info-value">{doc.observations}</span>
                  </div>
                )}
                {doc.bonCommande && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><ShoppingCart size={14} /> Bon de commande</span>
                    <button className="link-text" onClick={() => navigate(`/documents/${doc.bonCommande}`)}>
                      {doc.bonCommande}
                    </button>
                  </div>
                )}
                {doc.facture && (
                  <div className="detail-info-row">
                    <span className="detail-info-label"><FileText size={14} /> Facture</span>
                    <button className="link-text" onClick={() => navigate(`/documents/${doc.facture}`)}>
                      {doc.facture}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Montants (facture / BC) */}
        {(isFacture || isBC) && (
          <div className="detail-card">
            <h2 className="detail-card-title">
              <CreditCard size={18} /> Montants
            </h2>
            <div className="detail-amounts">
              <div className="detail-amount-row">
                <span>Total HT</span>
                <span>{formatMontant(doc.montantHT)}</span>
              </div>
              <div className="detail-amount-row">
                <span>TVA (20%)</span>
                <span>{formatMontant(doc.tva)}</span>
              </div>
              <div className="detail-amount-row detail-amount-total">
                <span>Total TTC</span>
                <span>{formatMontant(doc.montantTTC)}</span>
              </div>
            </div>
            {isFacture && doc.statut !== 'payee' && (
              <button className="btn-payment" onClick={() => window.open('#paiement-cb', '_blank')}>
                <CreditCard size={16} />
                Payer cette facture
                <ExternalLink size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lignes détail */}
      <div className="detail-card" style={{ marginTop: '1.5rem' }}>
        <h2 className="detail-card-title">
          <Package size={18} /> {isBL ? 'Détail de la livraison' : 'Détail des matériaux'}
        </h2>
        <table className="data-table">
          <thead>
            {isBL ? (
              <tr>
                <th>Matériau</th>
                <th style={{ textAlign: 'right' }}>Qté commandée</th>
                <th style={{ textAlign: 'right' }}>Qté livrée</th>
                <th>Unité</th>
                <th style={{ textAlign: 'right' }}>Écart</th>
              </tr>
            ) : (
              <tr>
                <th>Matériau</th>
                <th style={{ textAlign: 'right' }}>Quantité</th>
                <th>Unité</th>
                <th style={{ textAlign: 'right' }}>Prix unitaire</th>
                <th style={{ textAlign: 'right' }}>Montant HT</th>
              </tr>
            )}
          </thead>
          <tbody>
            {isBL ? (
              doc.lignes.map((ligne, idx) => {
                const ecart = ligne.quantiteLivree - ligne.quantiteCommandee;
                return (
                  <tr key={idx}>
                    <td style={{ fontWeight: 500 }}>{ligne.materiau}</td>
                    <td style={{ textAlign: 'right' }}>{ligne.quantiteCommandee.toFixed(1)}</td>
                    <td style={{ textAlign: 'right' }}>{ligne.quantiteLivree.toFixed(1)}</td>
                    <td>{ligne.unite}</td>
                    <td style={{ 
                      textAlign: 'right', 
                      color: ecart > 0 ? '#059669' : ecart < 0 ? '#DC2626' : '#6B7280',
                      fontWeight: 500 
                    }}>
                      {ecart > 0 ? '+' : ''}{ecart.toFixed(1)} {ligne.unite}
                    </td>
                  </tr>
                );
              })
            ) : (
              doc.lignes.map((ligne, idx) => (
                <tr key={idx}>
                  <td style={{ fontWeight: 500 }}>{ligne.materiau}</td>
                  <td style={{ textAlign: 'right' }}>{ligne.quantite}</td>
                  <td>{ligne.unite}</td>
                  <td style={{ textAlign: 'right' }}>{formatMontant(ligne.prixUnit)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(ligne.montant)}</td>
                </tr>
              ))
            )}
          </tbody>
          {!isBL && (
            <tfoot>
              <tr>
                <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>Total HT</td>
                <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.05em' }}>{formatMontant(doc.montantHT)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default DocumentDetail;
